import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SalonCard from '../components/SalonCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Scissors, Sparkles, Clock, Shield, Star, ChevronRight, MapPin } from 'lucide-react';

const BACKEND_URL = "https://zenvy-backend-e0zx.onrender.com";
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const { setMenTheme, setWomenTheme, setNeutralTheme } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredSalons, setFeaturedSalons] = useState([]);
  const [menSalons, setMenSalons] = useState([]);
  const [womenSalons, setWomenSalons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setNeutralTheme();
    fetchSalons();
  }, []);

  const fetchSalons = async () => {
    try {
      const [featuredRes, menRes, womenRes] = await Promise.all([
        fetch(`${API}/salons?featured=true&limit=4`),
        fetch(`${API}/salons?category=men&limit=3`),
        fetch(`${API}/salons?category=women&limit=3`)
      ]);
      
      if (featuredRes.ok) setFeaturedSalons(await featuredRes.json());
      if (menRes.ok) setMenSalons(await menRes.json());
      if (womenRes.ok) setWomenSalons(await womenRes.json());
    } catch (error) {
      console.error('Failed to fetch salons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/salons?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleMenClick = () => {
    setMenTheme();
    navigate('/salons/men');
  };

  const handleWomenClick = () => {
    setWomenTheme();
    navigate('/salons/women');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Split Hero Section */}
      <section className="pt-16" data-testid="hero-section">
        <div className="split-hero min-h-[90vh]">
          {/* Men's Side */}
          <div 
            className="split-hero-men relative flex items-center justify-center cursor-pointer group"
            onClick={handleMenClick}
            data-testid="hero-men"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
              style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1629881543350-9c14f791df99?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
            <div className="relative z-10 text-center px-8 py-20">
              <span className="inline-block px-4 py-1 bg-blue-600/20 text-blue-400 text-sm font-semibold tracking-widest uppercase mb-6 border border-blue-600/30">
                For Him
              </span>
              <h2 className="font-heading-men text-5xl md:text-7xl font-bold text-white uppercase tracking-wider mb-4">
                Men's Grooming
              </h2>
              <p className="text-zinc-400 text-lg max-w-md mx-auto mb-8">
                Precision cuts, classic shaves, and expert grooming for the modern gentleman.
              </p>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white uppercase tracking-widest font-bold rounded-none px-10 py-6 text-lg group-hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-shadow duration-300"
                data-testid="explore-men-btn"
              >
                EXPLORE <ChevronRight className="ml-2 w-5 h-5 inline" />
              </Button>
            </div>
          </div>

          {/* Women's Side */}
          <div 
            className="split-hero-women relative flex items-center justify-center cursor-pointer group"
            onClick={handleWomenClick}
            data-testid="hero-women"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
              style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1670201203150-bf8771401590?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rose-50 via-rose-50/60 to-transparent" />
            <div className="relative z-10 text-center px-8 py-20">
              <span className="inline-block px-4 py-1 bg-pink-600/20 text-pink-600 text-sm font-semibold tracking-widest uppercase mb-6 border border-pink-600/30 rounded-full">
                For Her
              </span>
              <h2 className="font-heading-women text-5xl md:text-7xl font-semibold text-rose-900 italic mb-4">
                Women's Beauty
              </h2>
              <p className="text-rose-700 text-lg max-w-md mx-auto mb-8">
                Luxurious treatments, stunning styles, and transformative beauty experiences.
              </p>
              <Button 
                className="bg-pink-600 hover:bg-pink-700 text-white font-serif italic rounded-full px-10 py-6 text-lg shadow-lg group-hover:shadow-[0_10px_40px_-10px_rgba(219,39,119,0.5)] transition-shadow duration-300"
                data-testid="explore-women-btn"
              >
                Explore <ChevronRight className="ml-2 w-5 h-5 inline" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-16 bg-zinc-50" data-testid="search-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-2">Find Your Perfect Salon</h2>
            <p className="text-zinc-600">Search by name, service, or location</p>
          </div>
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <Input 
                type="text"
                placeholder="Search salons, services, locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 text-lg bg-white border-zinc-200"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="px-8 bg-zinc-900 hover:bg-zinc-800" data-testid="search-btn">
              Search
            </Button>
          </form>
        </div>
      </section>

      {/* Featured Salons */}
      <section className="py-20 bg-white" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-sm font-semibold text-zinc-500 uppercase tracking-wider">Handpicked</span>
              <h2 className="text-4xl font-bold text-zinc-900 mt-2">Featured Salons</h2>
            </div>
            <Link to="/salons" className="text-zinc-900 font-medium flex items-center gap-1 hover:gap-2 transition-all">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-zinc-100 rounded-xl h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredSalons.map(salon => (
                <SalonCard key={salon.salon_id} salon={salon} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Men's Salons Section */}
      <section className="py-20 bg-zinc-950" data-testid="men-salons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-sm font-semibold text-blue-500 uppercase tracking-wider font-heading-men">For Him</span>
              <h2 className="text-4xl font-bold text-white mt-2 font-heading-men uppercase tracking-wide">Men's Salons</h2>
            </div>
            <Link to="/salons/men" className="text-blue-400 font-medium flex items-center gap-1 hover:gap-2 transition-all">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1,2,3].map(i => (
                <div key={i} className="bg-zinc-800 h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {menSalons.map(salon => (
                <SalonCard key={salon.salon_id} salon={salon} theme="men" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Women's Salons Section */}
      <section className="py-20 bg-rose-50" data-testid="women-salons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-sm font-semibold text-pink-600 uppercase tracking-wider">For Her</span>
              <h2 className="text-4xl font-semibold text-rose-900 mt-2 font-heading-women italic">Women's Salons</h2>
            </div>
            <Link to="/salons/women" className="text-pink-600 font-medium flex items-center gap-1 hover:gap-2 transition-all">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1,2,3].map(i => (
                <div key={i} className="bg-white rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {womenSalons.map(salon => (
                <SalonCard key={salon.salon_id} salon={salon} theme="women" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-zinc-500 uppercase tracking-wider">Simple Process</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-2">How It Works</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: Search, title: 'Search', desc: 'Find salons near you or by service type' },
              { icon: Scissors, title: 'Choose', desc: 'Select your preferred salon and service' },
              { icon: Clock, title: 'Book', desc: 'Pick a convenient date and time slot' },
              { icon: Sparkles, title: 'Enjoy', desc: 'Visit and experience premium service' }
            ].map((step, i) => (
              <div key={i} className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-zinc-100 flex items-center justify-center group-hover:bg-zinc-900 transition-colors duration-300">
                  <step.icon className="w-10 h-10 text-zinc-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <div className="text-sm font-bold text-zinc-400 mb-2">Step {i + 1}</div>
                <h3 className="text-xl font-bold text-zinc-900 mb-2">{step.title}</h3>
                <p className="text-zinc-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-zinc-950" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-blue-500 uppercase tracking-wider">Our Promise</span>
            <h2 className="text-4xl font-bold text-white mt-2">Why Choose Zenvy</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Verified Salons', desc: 'Every salon is vetted for quality, hygiene, and professional service standards.' },
              { icon: Star, title: 'Real Reviews', desc: 'Authentic customer reviews to help you make informed decisions.' },
              { icon: Clock, title: 'Easy Booking', desc: 'Book in seconds with real-time availability and instant confirmation.' }
            ].map((feature, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 p-8 hover:border-blue-500/50 transition-colors duration-300">
                <feature.icon className="w-12 h-12 text-blue-500 mb-6" />
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-zinc-900 to-zinc-800" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Look?
          </h2>
          <p className="text-xl text-zinc-400 mb-10">
            Join thousands of satisfied customers who trust Zenvy for their grooming needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/salons">
              <Button className="px-8 py-6 text-lg bg-white text-zinc-900 hover:bg-zinc-100" data-testid="cta-book-btn">
                Book Now
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="px-8 py-6 text-lg border-zinc-600 text-white hover:bg-zinc-800" data-testid="cta-partner-btn">
                Become a Partner
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
